import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, postingClient, type Lane, type VinDecodeResponse } from '../lib/api'
import { normalizeZipInput, useZipLocation } from '../lib/useZipLocation'

const VEHICLE_TYPES = ['SEDAN', 'SUV', 'TRUCK', 'VAN', 'MOTORCYCLE', 'OTHER']

interface VehicleForm {
  vin: string; make: string; model: string; year: string; color: string; type: string
}

interface VinDecodeUiState {
  status: 'idle' | 'loading' | 'success' | 'error' | 'invalid'
  message: string | null
  engine?: string | null
  fuelType?: string | null
  bodyClass?: string | null
}

interface MileagePreviewData {
  estDistanceMiles: number | null
  mileageProvider: string
  mileageSource: string
  mileageDurationSeconds?: number | null
  customerRatePerMileCents: number | null
  carrierRatePerMileCents: number | null
  marginPerMileCents: number | null
  warnings: string[]
}

const emptyVehicle = (): VehicleForm => ({ vin: '', make: '', model: '', year: '', color: '', type: 'SEDAN' })
const emptyVinDecodeState = (): VinDecodeUiState => ({ status: 'idle', message: null })

function normalizeVinInput(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17)
}

function isValidVinFormat(value: string): boolean {
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(value)
}

function normalizeDecodedVehicleType(decoded: VinDecodeResponse): string | null {
  const fromApi = (decoded.vehicleType || decoded.bodyClass || '').toUpperCase()
  if (!fromApi) return null
  if (fromApi.includes('SUV') || fromApi.includes('SPORT UTILITY')) return 'SUV'
  if (fromApi.includes('TRUCK') || fromApi.includes('PICKUP')) return 'TRUCK'
  if (fromApi.includes('VAN')) return 'VAN'
  if (fromApi.includes('MOTORCYCLE')) return 'MOTORCYCLE'
  if (fromApi.includes('SEDAN') || fromApi.includes('COUPE') || fromApi.includes('WAGON') || fromApi.includes('HATCHBACK')) return 'SEDAN'
  return 'OTHER'
}

function buildRouteDisplay(city: string, state: string, zip: string): string {
  const cityPart = city.trim()
  const statePart = state.trim().toUpperCase()
  const zipPart = zip.trim()
  if (cityPart && statePart) return `${cityPart}, ${statePart} ${zipPart}`
  if (statePart) return `${statePart} ${zipPart}`
  if (cityPart) return `${cityPart} ${zipPart}`
  return zipPart
}

export default function CreateLoadPage() {
  const navigate = useNavigate()
  const [originZip, setOriginZip] = useState('')
  const [originCity, setOriginCity] = useState('')
  const [originState, setOriginState] = useState('')
  const [destinationZip, setDestinationZip] = useState('')
  const [destinationCity, setDestinationCity] = useState('')
  const [destinationState, setDestinationState] = useState('')
  const [pickupDate, setPickupDate] = useState('')
  const [deliveryDate, setDeliveryDate] = useState('')
  const [manualMiles, setManualMiles] = useState('')
  const [customerPay, setCustomerPay] = useState('')
  const [carrierPay, setCarrierPay] = useState('')
  const [notes, setNotes] = useState('')
  const [referenceNumber, setReferenceNumber] = useState('')
  const [vehicles, setVehicles] = useState<VehicleForm[]>([emptyVehicle()])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lanes, setLanes] = useState<Lane[]>([])
  const [selectedLaneId, setSelectedLaneId] = useState('')
  const [mileageLoading, setMileageLoading] = useState(false)
  const [mileageData, setMileageData] = useState<MileagePreviewData | null>(null)
  const [mileageWarnings, setMileageWarnings] = useState<string[]>([])
  const [vinDecodeStates, setVinDecodeStates] = useState<VinDecodeUiState[]>([emptyVinDecodeState()])
  const [vehicleFieldTouched, setVehicleFieldTouched] = useState<Array<Partial<Record<keyof VehicleForm, boolean>>>>([{}])
  const lastDecodedVinPerVehicleRef = useRef<string[]>([''])
  const originZipLookup = useZipLocation({ zip: originZip, onCity: setOriginCity, onState: setOriginState, lookupZip: api.geo.zipLookup })
  const destinationZipLookup = useZipLocation({ zip: destinationZip, onCity: setDestinationCity, onState: setDestinationState, lookupZip: api.geo.zipLookup })

  useEffect(() => {
    const loadLanes = async () => {
      try {
        const response = await api.lanes.list({ status: 'ACTIVE' })
        setLanes(response.data)
      } catch {
        setLanes([])
      }
    }
    void loadLanes()
  }, [])

  const handleLanePrefill = (laneId: string) => {
    setSelectedLaneId(laneId)
    if (!laneId) return
    const lane = lanes.find((entry) => entry.id === laneId)
    if (!lane) return

    if (lane.originZip) setOriginZip(lane.originZip)
    if (lane.originCity) setOriginCity(lane.originCity)
    if (lane.originState) setOriginState(lane.originState)
    if (lane.destinationZip) setDestinationZip(lane.destinationZip)
    if (lane.destinationCity) setDestinationCity(lane.destinationCity)
    if (lane.destinationState) setDestinationState(lane.destinationState)
    if (lane.estDistanceMiles != null) setManualMiles(String(lane.estDistanceMiles))
    if (lane.targetCustomerPayCents != null) setCustomerPay((lane.targetCustomerPayCents / 100).toFixed(2))
    if (lane.targetCarrierPayCents != null) setCarrierPay((lane.targetCarrierPayCents / 100).toFixed(2))
    if (lane.notes && !notes) setNotes(lane.notes)
  }

  const updateVehicle = (i: number, field: keyof VehicleForm, value: string, source: 'manual' | 'autofill' = 'manual') => {
    setVehicles(v => v.map((veh, idx) => idx === i ? { ...veh, [field]: value } : veh))
    if (source === 'manual' && field !== 'vin') {
      setVehicleFieldTouched((prev) => prev.map((entry, idx) => (
        idx === i ? { ...entry, [field]: true } : entry
      )))
    }
  }

  const shouldAutofillVehicleField = (i: number, field: keyof VehicleForm, proposedValue: string): boolean => {
    if (!proposedValue.trim()) return false
    const currentValue = vehicles[i]?.[field] ?? ''
    const touched = !!vehicleFieldTouched[i]?.[field]
    if (!currentValue.trim()) return true
    if (field === 'type' && currentValue === 'SEDAN') return true
    if (!touched) return true
    return false
  }

  const applyDecodedVehicleData = (i: number, decoded: VinDecodeResponse) => {
    const nextYear = decoded.year?.trim() ?? ''
    const nextMake = decoded.make?.trim() ?? ''
    const nextModel = decoded.model?.trim() ?? ''
    const nextType = normalizeDecodedVehicleType(decoded) ?? ''

    if (shouldAutofillVehicleField(i, 'year', nextYear)) updateVehicle(i, 'year', nextYear, 'autofill')
    if (shouldAutofillVehicleField(i, 'make', nextMake)) updateVehicle(i, 'make', nextMake, 'autofill')
    if (shouldAutofillVehicleField(i, 'model', nextModel)) updateVehicle(i, 'model', nextModel, 'autofill')
    if (shouldAutofillVehicleField(i, 'type', nextType)) updateVehicle(i, 'type', nextType, 'autofill')
  }

  const setVinDecodeState = (i: number, state: VinDecodeUiState) => {
    setVinDecodeStates((prev) => prev.map((entry, idx) => (idx === i ? state : entry)))
  }

  const decodeVinForVehicle = async (i: number, vin: string) => {
    if (!isValidVinFormat(vin)) {
      setVinDecodeState(i, { status: 'invalid', message: 'Invalid VIN format' })
      return
    }
    setVinDecodeState(i, { status: 'loading', message: 'Decoding VIN...' })
    try {
      const response = await api.vehicles.decodeVin(vin)
      const decoded = response.data
      if (!decoded.valid) {
        setVinDecodeState(i, { status: 'error', message: decoded.error || 'VIN could not be decoded' })
        return
      }
      applyDecodedVehicleData(i, decoded)
      const details = [decoded.engine, decoded.fuelType].filter(Boolean).join(' | ')
      setVinDecodeState(i, {
        status: 'success',
        message: 'VIN decoded',
        engine: decoded.engine,
        fuelType: decoded.fuelType,
        bodyClass: decoded.bodyClass,
      })
      if (details) {
        setVinDecodeState(i, {
          status: 'success',
          message: `VIN decoded (${details})`,
          engine: decoded.engine,
          fuelType: decoded.fuelType,
          bodyClass: decoded.bodyClass,
        })
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || 'VIN could not be decoded'
      setVinDecodeState(i, { status: 'error', message })
    }
  }

  const handleVehicleVinChange = (i: number, rawValue: string) => {
    const normalized = normalizeVinInput(rawValue)
    updateVehicle(i, 'vin', normalized)

    if (!normalized) {
      setVinDecodeState(i, emptyVinDecodeState())
      lastDecodedVinPerVehicleRef.current[i] = ''
      return
    }

    if (normalized.length < 17) {
      setVinDecodeState(i, { status: 'invalid', message: 'Invalid VIN format' })
      lastDecodedVinPerVehicleRef.current[i] = ''
      return
    }

    if (!isValidVinFormat(normalized)) {
      setVinDecodeState(i, { status: 'invalid', message: 'Invalid VIN format' })
      return
    }

    if (lastDecodedVinPerVehicleRef.current[i] === normalized) return
    lastDecodedVinPerVehicleRef.current[i] = normalized
    void decodeVinForVehicle(i, normalized)
  }

  const addVehicle = () => {
    setVehicles(v => [...v, emptyVehicle()])
    setVinDecodeStates(v => [...v, emptyVinDecodeState()])
    setVehicleFieldTouched(v => [...v, {}])
    lastDecodedVinPerVehicleRef.current.push('')
  }
  const removeVehicle = (i: number) => {
    setVehicles(v => v.filter((_, idx) => idx !== i))
    setVinDecodeStates(v => v.filter((_, idx) => idx !== i))
    setVehicleFieldTouched(v => v.filter((_, idx) => idx !== i))
    lastDecodedVinPerVehicleRef.current = lastDecodedVinPerVehicleRef.current.filter((_, idx) => idx !== i)
  }

  const customerPayNum = customerPay ? Number(customerPay) : null
  const carrierPayNum = carrierPay ? Number(carrierPay) : null
  const manualMilesNum = manualMiles ? Number(manualMiles) : null
  const hasManualMilesOverride = typeof manualMilesNum === 'number' && Number.isFinite(manualMilesNum) && manualMilesNum >= 0
  const estDistanceMilesNum = hasManualMilesOverride
    ? manualMilesNum
    : mileageData?.estDistanceMiles ?? null

  const margin =
    typeof customerPayNum === 'number' && Number.isFinite(customerPayNum) &&
    typeof carrierPayNum === 'number' && Number.isFinite(carrierPayNum)
      ? customerPayNum - carrierPayNum
      : null
  const marginPercent =
    margin !== null && customerPayNum && customerPayNum > 0
      ? (margin / customerPayNum) * 100
      : null
  const customerRpm = !hasManualMilesOverride && mileageData?.customerRatePerMileCents
    ? mileageData.customerRatePerMileCents / 100
    : typeof customerPayNum === 'number' && Number.isFinite(customerPayNum) &&
      typeof estDistanceMilesNum === 'number' && Number.isFinite(estDistanceMilesNum) && estDistanceMilesNum > 0
      ? customerPayNum / estDistanceMilesNum
      : null
  const carrierRpm = !hasManualMilesOverride && mileageData?.carrierRatePerMileCents
    ? mileageData.carrierRatePerMileCents / 100
    : typeof carrierPayNum === 'number' && Number.isFinite(carrierPayNum) &&
      typeof estDistanceMilesNum === 'number' && Number.isFinite(estDistanceMilesNum) && estDistanceMilesNum > 0
      ? carrierPayNum / estDistanceMilesNum
      : null

  const handleCalculateMileage = async () => {
    if (!originZip.match(/^\d{5}$/) || !destinationZip.match(/^\d{5}$/)) {
      setError('Please enter valid 5-digit ZIPs for both origin and destination')
      return
    }

    setMileageLoading(true)
    setError(null)
    try {
      const res = await postingClient.post<MileagePreviewData>('/loads/mileage-preview', {
        originZip: originZip.trim(),
        originCity: originCity || undefined,
        originState: originState || undefined,
        destinationZip: destinationZip.trim(),
        destinationCity: destinationCity || undefined,
        destinationState: destinationState || undefined,
        customerPayCents: customerPayNum ? Math.round(customerPayNum * 100) : undefined,
        carrierPayCents: carrierPayNum ? Math.round(carrierPayNum * 100) : undefined,
        manualMiles: null,
      })
      setMileageData(res.data)
      setMileageWarnings(res.data.warnings || [])
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to calculate mileage')
    } finally {
      setMileageLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!originZip || !destinationZip) { setError('Origin and destination ZIPs are required.'); return }
    if (vehicles.some(v => !v.vin.trim())) { setError('All vehicles must have a VIN.'); return }
    if (!/^\d{5}(-\d{4})?$/.test(originZip.trim())) { setError('Origin ZIP must be a valid US ZIP code.'); return }
    if (!/^\d{5}(-\d{4})?$/.test(destinationZip.trim())) { setError('Destination ZIP must be a valid US ZIP code.'); return }
    if (customerPayNum !== null && (!Number.isFinite(customerPayNum) || customerPayNum < 0)) { setError('Customer Pay must be >= 0.'); return }
    if (carrierPayNum !== null && (!Number.isFinite(carrierPayNum) || carrierPayNum < 0)) { setError('Carrier Pay must be >= 0.'); return }

    setLoading(true)
    try {
      const originDisplay = buildRouteDisplay(originCity, originState, originZip)
      const destinationDisplay = buildRouteDisplay(destinationCity, destinationState, destinationZip)
      const res = await api.loads.create({
        origin: originDisplay,
        destination: destinationDisplay,
        originZip: originZip.trim(),
        originCity: originCity || undefined,
        originState: originState || undefined,
        destinationZip: destinationZip.trim(),
        destinationCity: destinationCity || undefined,
        destinationState: destinationState || undefined,
        estDistanceMiles: estDistanceMilesNum ?? undefined,
        pickupDate: pickupDate || undefined,
        deliveryDate: deliveryDate || undefined,
        customerPay: customerPayNum ?? undefined,
        carrierPay: carrierPayNum ?? undefined,
        totalPay: customerPayNum ?? undefined,
        notes: notes || undefined,
        vehicles: vehicles.map(v => ({
          vin: v.vin.trim().toUpperCase(),
          make: v.make || undefined,
          model: v.model || undefined,
          year: v.year ? parseInt(v.year) : undefined,
          color: v.color || undefined,
          type: v.type,
          isRunning: true,
          hasKeys: true,
        })),
      })
      navigate(`/loads/${res.data.loadId}`)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to create load. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-100 px-4 py-8">
      <div className="mx-auto w-full max-w-5xl">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-slate-900">Post a Load</h1>
          <p className="mt-1 text-sm text-slate-500">Add load details to post to the board and find the right carrier.</p>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Lane Prefill</label>
            <select
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              value={selectedLaneId}
              onChange={e => handleLanePrefill(e.target.value)}
            >
              <option value="">Select lane (optional)</option>
              {lanes.map((lane) => (
                <option key={lane.id} value={lane.id}>
                  {lane.laneNumber} · {lane.customerName ?? 'No customer'} · {lane.originZip ?? '-'} to {lane.destinationZip ?? '-'}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-3">
              <h2 className="text-xl font-semibold text-slate-900">Pickup</h2>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">ZIP</label>
                <input
                  value={originZip}
                  onChange={e => setOriginZip(normalizeZipInput(e.target.value))}
                  required
                  maxLength={10}
                  inputMode="numeric"
                  placeholder="64062"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                {originZipLookup.loading && <p className="mt-1 text-xs text-slate-500" role="status">Looking up ZIP...</p>}
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">City</label>
                <input
                  value={originCity}
                  onChange={e => setOriginCity(e.target.value)}
                  placeholder="Lawson"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">State</label>
                <input
                  value={originState}
                  onChange={e => setOriginState(e.target.value)}
                  maxLength={2}
                  placeholder="MO"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>

            <div className="space-y-3">
              <h2 className="text-xl font-semibold text-slate-900">Delivery</h2>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">ZIP</label>
                <input
                  value={destinationZip}
                  onChange={e => setDestinationZip(normalizeZipInput(e.target.value))}
                  required
                  maxLength={10}
                  inputMode="numeric"
                  placeholder="80918"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                {destinationZipLookup.loading && <p className="mt-1 text-xs text-slate-500" role="status">Looking up ZIP...</p>}
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">City</label>
                <input
                  value={destinationCity}
                  onChange={e => setDestinationCity(e.target.value)}
                  placeholder="Colorado Springs"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">State</label>
                <input
                  value={destinationState}
                  onChange={e => setDestinationState(e.target.value)}
                  maxLength={2}
                  placeholder="CO"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>
          </div>

          {(originZipLookup.message || destinationZipLookup.message) && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="status">
              {originZipLookup.message ?? destinationZipLookup.message}
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="min-w-[180px]">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Miles</div>
                <div className="mt-1 text-2xl font-bold text-slate-900">
                  {typeof estDistanceMilesNum === 'number' && Number.isFinite(estDistanceMilesNum)
                    ? estDistanceMilesNum.toLocaleString()
                    : '--'}
                  <span className="ml-1 text-base font-medium text-slate-500">mi</span>
                </div>
                {mileageData && !hasManualMilesOverride && (
                  <p className="text-xs text-slate-500">
                    source: {mileageData.mileageSource} · provider: {mileageData.mileageProvider}
                  </p>
                )}
                {hasManualMilesOverride && (
                  <p className="text-xs text-blue-700">manual override active</p>
                )}
              </div>
              <div className="flex flex-col gap-2 md:min-w-[320px]">
                <button
                  type="button"
                  onClick={handleCalculateMileage}
                  disabled={mileageLoading}
                  className="rounded-lg bg-blue-700 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
                >
                  {mileageLoading ? 'Calculating...' : 'Calculate Routed Miles'}
                </button>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={manualMiles}
                  onChange={e => setManualMiles(e.target.value)}
                  placeholder="Manual miles override"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>
            {mileageWarnings.length > 0 && (
              <p className="mt-2 border-t border-slate-200 pt-2 text-xs text-amber-700">{mileageWarnings.join('; ')}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Pickup Date</label>
                <input
                  type="date"
                  value={pickupDate}
                  onChange={e => setPickupDate(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Customer Pay</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={customerPay}
                  onChange={e => setCustomerPay(e.target.value)}
                  placeholder="400.00"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Carrier Pay</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={carrierPay}
                  onChange={e => setCarrierPay(e.target.value)}
                  placeholder="300.00"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Delivery Date</label>
                <input
                  type="date"
                  value={deliveryDate}
                  onChange={e => setDeliveryDate(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Customer RPM</div>
                  <div className="text-xl font-semibold text-slate-900">{customerRpm === null ? '—' : `$${customerRpm.toFixed(2)}`}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Margin $</div>
                  <div className="text-xl font-semibold text-slate-900">
                    {margin === null ? '—' : `$${margin.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Carrier RPM</div>
                  <div className="text-xl font-semibold text-slate-900">{carrierRpm === null ? '—' : `$${carrierRpm.toFixed(2)}`}</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Margin %</div>
                  <div className="text-xl font-semibold text-slate-900">{marginPercent === null ? '—' : `${marginPercent.toFixed(2)}%`}</div>
                </div>
              </div>
            </div>
          </div>

          {customerPayNum !== null && carrierPayNum !== null && carrierPayNum > customerPayNum && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Carrier Pay is higher than Customer Pay. This load has negative margin.
            </div>
          )}

          <div className="space-y-3 border-t border-slate-200 pt-5">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Vehicle Details</h2>
              <button type="button" onClick={addVehicle} className="text-sm font-medium text-blue-700 hover:text-blue-900">+ Add Vehicle</button>
            </div>
            {vehicles.map((v, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">Vehicle {i + 1}</span>
                  {vehicles.length > 1 && (
                    <button type="button" onClick={() => removeVehicle(i)} className="text-xs font-medium text-red-600 hover:text-red-700">Remove</button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">VIN <span className="text-red-500">*</span></label>
                    <input
                      value={v.vin}
                      onChange={e => handleVehicleVinChange(i, e.target.value)}
                      required
                      maxLength={17}
                      placeholder="1FMJK2A58EEF00711"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    />
                    {vinDecodeStates[i]?.message && (
                      <p
                        className={`mt-1 text-xs ${
                          vinDecodeStates[i]?.status === 'success'
                            ? 'text-green-700'
                            : vinDecodeStates[i]?.status === 'loading'
                              ? 'text-slate-500'
                              : 'text-amber-700'
                        }`}
                      >
                        {vinDecodeStates[i]?.message}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Year</label>
                      <input
                        type="number"
                        min="1900"
                        max="2030"
                        value={v.year}
                        onChange={e => updateVehicle(i, 'year', e.target.value)}
                        placeholder="2014"
                        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Make</label>
                      <input
                        value={v.make}
                        onChange={e => updateVehicle(i, 'make', e.target.value)}
                        placeholder="Ford"
                        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Model</label>
                      <input
                        value={v.model}
                        onChange={e => updateVehicle(i, 'model', e.target.value)}
                        placeholder="Expedition"
                        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Type</label>
                      <select
                        value={v.type}
                        onChange={e => updateVehicle(i, 'type', e.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      >
                        {VEHICLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Color</label>
                  <input
                    value={v.color}
                    onChange={e => updateVehicle(i, 'color', e.target.value)}
                    placeholder="White"
                    className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3 border-t border-slate-200 pt-5">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                placeholder="Runs and drives. No warning lights. Clean title in hand."
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">Reference #</label>
              <input
                value={referenceNumber}
                onChange={e => setReferenceNumber(e.target.value)}
                placeholder="REF-05222025-001"
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-lg bg-blue-700 py-3 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
            >
              {loading ? 'Posting load…' : 'Post Load to Board'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/loads')}
              className="flex-1 rounded-lg border border-blue-300 bg-white py-3 text-sm font-semibold text-blue-700 hover:bg-blue-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
